import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, TrashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';

const DeleteAccountPage = () => {
  const { i18n } = useTranslation();
  const { theme } = useTheme();
  const isHr = i18n.language?.startsWith('hr');

  const content = isHr ? {
    title: 'Brisanje računa - ProTrener',
    heading: 'Kako izbrisati svoj ProTrener račun',
    intro: 'ProTrener vam omogućuje potpunu kontrolu nad vašim podacima. Možete trajno izbrisati svoj račun u bilo kojem trenutku izravno iz aplikacije.',
    stepsTitle: 'Koraci za brisanje računa:',
    steps: [
      'Otvorite ProTrener aplikaciju i prijavite se na svoj račun',
      'Idite na svoj Dashboard (Početna stranica)',
      'Pritisnite ikonu zupčanika (⚙️) ili "Postavke"',
      'Pomaknite se do dna i pronađite "Izbriši račun"',
      'Unesite svoju lozinku za potvrdu',
      'Upišite "DELETE" za potvrdu brisanja',
      'Pritisnite "Izbriši račun"'
    ],
    dataDeleted: 'Podaci koji se brišu:',
    dataDeletedList: [
      'Vaš profil i osobni podaci',
      'Povijest treninga i napretka',
      'Sve poruke s trenerom',
      'Fotografije napretka',
      'Postavke i preferencije'
    ],
    dataKept: 'Podaci koji se zadržavaju:',
    dataKeptList: [
      'Anonimizirana statistika korištenja (bez osobnih podataka)',
      'Povijest transakcija za računovodstvene svrhe (zakonska obveza)'
    ],
    warning: 'Upozorenje',
    warningText: 'Ovo je trajna radnja. Nakon brisanja računa, svi vaši podaci bit će nepovratno izgubljeni i ne mogu se vratiti.',
    contact: 'Trebate pomoć?',
    contactText: 'Ako imate problema s brisanjem računa ili pitanja o vašim podacima, kontaktirajte nas na:',
    email: 'info@protrener.app',
    backToLogin: '← Natrag na prijavu'
  } : {
    title: 'Delete Account - ProTrener',
    heading: 'How to Delete Your ProTrener Account',
    intro: 'ProTrener gives you full control over your data. You can permanently delete your account at any time directly from the app.',
    stepsTitle: 'Steps to delete your account:',
    steps: [
      'Open the ProTrener app and log in to your account',
      'Go to your Dashboard (Home page)',
      'Tap the gear icon (⚙️) or "Settings"',
      'Scroll down and find "Delete Account"',
      'Enter your password to confirm your identity',
      'Type "DELETE" to confirm deletion',
      'Tap "Delete Account"'
    ],
    dataDeleted: 'Data that will be deleted:',
    dataDeletedList: [
      'Your profile and personal information',
      'Training history and progress',
      'All messages with your trainer',
      'Progress photos',
      'Settings and preferences'
    ],
    dataKept: 'Data that is retained:',
    dataKeptList: [
      'Anonymized usage statistics (no personal data)',
      'Transaction history for accounting purposes (legal requirement)'
    ],
    warning: 'Warning',
    warningText: 'This is a permanent action. Once your account is deleted, all your data will be permanently lost and cannot be recovered.',
    contact: 'Need help?',
    contactText: 'If you have trouble deleting your account or questions about your data, contact us at:',
    email: 'info@protrener.app',
    backToLogin: '← Back to Login'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Helmet>
        <title>{content.title}</title>
        <meta name="description" content={content.intro} />
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Back Link */}
        <Link 
          to="/login" 
          className={`inline-flex items-center ${theme.sectionLink} mb-6 transition-colors`}
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          {content.backToLogin}
        </Link>

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
              <TrashIcon className="h-6 w-6 text-red-600" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              {content.heading}
            </h1>
          </div>
          <p className="text-gray-600 text-lg">
            {content.intro}
          </p>
        </div>

        {/* Steps */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            {content.stepsTitle}
          </h2>
          <ol className="space-y-3">
            {content.steps.map((step, index) => (
              <li key={index} className="flex items-start">
                <span className={`flex-shrink-0 w-8 h-8 ${theme.activePill} rounded-full flex items-center justify-center font-bold text-sm mr-3`}>
                  {index + 1}
                </span>
                <span className="text-gray-700 pt-1">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Data Deleted */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            {content.dataDeleted}
          </h2>
          <ul className="space-y-2 mb-6">
            {content.dataDeletedList.map((item, index) => (
              <li key={index} className="flex items-center text-gray-700">
                <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                {item}
              </li>
            ))}
          </ul>

          <h2 className="text-xl font-bold text-gray-900 mb-4">
            {content.dataKept}
          </h2>
          <ul className="space-y-2">
            {content.dataKeptList.map((item, index) => (
              <li key={index} className="flex items-center text-gray-700">
                <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Warning */}
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 mb-6">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-red-800 mb-1">{content.warning}</h3>
              <p className="text-red-700">{content.warningText}</p>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {content.contact}
          </h2>
          <p className="text-gray-600 mb-3">
            {content.contactText}
          </p>
          <a 
            href={`mailto:${content.email}`}
            className={`${theme.sectionLink} font-semibold`}
          >
            {content.email}
          </a>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
